import {
  PricingSummary,
  PricingSummaryRange,
  Uplift,
  Discount,
  DiscountType,
  Disbursement,
  TaxLabelStatus as vatStatus,
  Range,
  Phase,
  PricingLevel,
  PricingType,
  PriceRange,
  UpliftType,
  pricingFieldsByType,
  EstimatedRangePricingFields,
  NoneRangePricingFields,
  Work,
} from "../../data-types";

import {
  sumLegalFeePriceSingle,
  sumLegalFeePriceRange,
  sumWorkPriceRange,
  sumWorkPriceSingle,
} from "./sum-legal-fee-costs";

interface Totals {
  legalFees: number | { lower: number; upper: number };
  subTotalExcludingVat: number | { lower: number; upper: number };
  vat: number | { lower: number; upper: number };
  subTotalIncludingVat: number | { lower: number; upper: number };
  grandTotal: number | { lower: number; upper: number };
  disbursementsTotal: number | { lower: number; upper: number };
  discountsTotal: number | { lower: number; upper: number };
  upliftsTotal: number | { lower: number; upper: number };
}

export const createTotals = (
  pricingSummary: EstimatedRangePricingFields | NoneRangePricingFields,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[],
  matterWork: Work[] | null,
  topDownRateDiscount: number,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  taxLevelPercent: number,
  adminCharge: number
): Totals => {
  if (isSingle(pricingSummary)) {
    return createFromSingle(
      pricingSummary,
      phases,
      disbursements,
      uplifts,
      discounts,
      matterWork,
      topDownRateDiscount,
      pricingLevel,
      pricingType,
      taxLevelPercent,
      adminCharge
    );
  } else {
    return createFromRange(
      pricingSummary,
      phases,
      disbursements,
      uplifts,
      discounts,
      pricingLevel,
      taxLevelPercent,
      adminCharge
    );
  }
};

const isSingle = (
  pricingSummary: PricingSummary | PricingSummaryRange
): pricingSummary is PricingSummary =>
  pricingSummary.chosen === null || typeof pricingSummary.chosen === "number";

const createFromSingle = (
  pricingSummary: NoneRangePricingFields,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[] | null,
  matterWork: Work[] | null,
  topDownRateDiscount: number,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  taxLevelPercent: number,
  adminCharge: number
): Totals => {
  const legalFees =
    pricingSummary.chosen ??
    sumLegalFeePriceSingle(
      pricingSummary,
      phases,
      pricingLevel,
      pricingType,
      matterWork,
      topDownRateDiscount
    );
  const upliftsTotal =
    uplifts?.reduce((acc, { amount, type }) => {
      return type === UpliftType.Amount ? acc + amount : acc + (amount * legalFees) / 100;
    }, 0) ?? 0;
  const discountsTotal =
    discounts?.reduce(
      (acc, { amount, type }) =>
        type === DiscountType.Percent
          ? acc - (amount * (legalFees + upliftsTotal)) / 100
          : acc - amount,
      0
    ) ?? 0;
  const subTotalExcludingVatBeforeAdmin = legalFees + upliftsTotal + discountsTotal;
  const adminChargeTotal = (adminCharge * subTotalExcludingVatBeforeAdmin) / 100;
  const subTotalExcludingVat = Math.round(subTotalExcludingVatBeforeAdmin + adminChargeTotal);
  const vat = (taxLevelPercent / 100) * (subTotalExcludingVat || 0);
  const subTotalIncludingVat = (subTotalExcludingVat || 0) + vat;
  const disbursementsTotal =
    disbursements?.reduce(
      (acc, curr) =>
        curr.vatStatus === vatStatus.Exclusive
          ? acc + (1 + taxLevelPercent / 100) * (curr.amount ?? 0)
          : acc + (curr.amount ?? 0),
      0
    ) ?? 0;
  const grandTotal = subTotalIncludingVat + disbursementsTotal;

  return {
    legalFees,
    discountsTotal,
    disbursementsTotal,
    upliftsTotal,
    subTotalExcludingVat,
    vat,
    subTotalIncludingVat,
    grandTotal,
  };
};

const convertSingleToString = (value: number, currencySymbol: string): string =>
  `${currencySymbol} ${Math.round(value).toLocaleString()}`;

const createFromRange = (
  pricingSummary: EstimatedRangePricingFields,
  phases: Phase[] | null,
  disbursements: Disbursement[] | null,
  uplifts: Uplift[] | null,
  discounts: Discount[] | null,
  pricingLevel: PricingLevel,
  taxLevelPercent: number,
  adminCharge: number
): Totals => {
  const legalFees = isPriceRangeDefined(pricingSummary.chosen)
    ? pricingSummary.chosen
    : sumLegalFeePriceRange(pricingSummary, phases, pricingLevel);
  const upliftsTotal = uplifts?.reduce(
    ({ lower, upper }, { amount, type }) => ({
      lower: type === UpliftType.Amount ? lower + amount : lower + (amount * legalFees.lower) / 100,
      upper: type === UpliftType.Amount ? upper + amount : upper + (amount * legalFees.upper) / 100,
    }),
    { lower: 0, upper: 0 }
  ) ?? { lower: 0, upper: 0 };

  const discountsTotal = discounts?.reduce(
    ({ lower, upper }, { amount, type }) => ({
      lower:
        type === DiscountType.Percent
          ? lower - (amount * (legalFees.lower + upliftsTotal.lower)) / 100
          : lower - (amount + upliftsTotal.lower),
      upper:
        type === DiscountType.Percent
          ? upper - (amount * (legalFees.upper + upliftsTotal.upper)) / 100
          : upper - (amount + upliftsTotal.upper),
    }),
    { lower: 0, upper: 0 }
  ) ?? { lower: 0, upper: 0 };

  const subTotalExcludingVatAndAdmin = {
    lower: legalFees.lower + upliftsTotal.lower + discountsTotal.lower,
    upper: legalFees.upper + upliftsTotal.upper + discountsTotal.upper,
  };
  const adminChargeTotal = {
    lower: (adminCharge * subTotalExcludingVatAndAdmin.lower) / 100,
    upper: (adminCharge * subTotalExcludingVatAndAdmin.upper) / 100,
  };

  const subTotalExcludingVat = {
    lower: Math.round(subTotalExcludingVatAndAdmin.lower + adminChargeTotal.lower),
    upper: Math.round(subTotalExcludingVatAndAdmin.upper + adminChargeTotal.upper),
  };

  const vat = {
    lower: (taxLevelPercent / 100) * (subTotalExcludingVat.lower || 0),
    upper: (taxLevelPercent / 100) * (subTotalExcludingVat.upper || 0),
  };
  const subTotalIncludingVat = {
    lower: subTotalExcludingVat.lower + vat.lower,
    upper: subTotalExcludingVat.upper + vat.upper,
  };
  const disbursementsTotal =
    disbursements?.reduce(
      (acc, curr) =>
        curr.vatStatus === vatStatus.Exclusive
          ? acc + (1 + taxLevelPercent / 100) * (curr.amount ?? 0)
          : acc + (curr.amount ?? 0) ?? 0,
      0
    ) ?? 0;
  const grandTotal = {
    lower: subTotalIncludingVat.lower + disbursementsTotal,
    upper: subTotalIncludingVat.upper + disbursementsTotal,
  };

  return {
    legalFees,
    discountsTotal,
    upliftsTotal,
    disbursementsTotal,
    subTotalExcludingVat,
    vat,
    subTotalIncludingVat,
    grandTotal,
  };
};

const convertRangeToString = (range: Range, currencySymbol: string): string =>
  `${currencySymbol} ${Math.round(
    range.lower || 0
  ).toLocaleString()} to ${currencySymbol} ${Math.round(range.upper || 0).toLocaleString()}`;

export const createPhaseTotalText = (
  phase: Phase,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  currencySymbol: string
): string => {
  if (pricingType === PricingType.None) {
    return "";
  }
  if (pricingLevel === PricingLevel.Phase) {
    if (pricingType === PricingType.EstimatedRange) {
      const tranformed = pricingFieldsByType(PricingType.EstimatedRange, phase.priceOptions);
      tranformed.chosen;
      return convertRangeToString(tranformed.chosen, currencySymbol);
    } else {
      const tranformed = pricingFieldsByType(pricingType, phase.priceOptions);
      return convertSingleToString(tranformed.chosen || 0, currencySymbol);
    }
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    if (pricingType === PricingType.EstimatedRange) {
      return convertRangeToString(sumWorkPriceRange(phase.work), currencySymbol);
    } else {
      return convertSingleToString(sumWorkPriceSingle(phase.work), currencySymbol);
    }
  }
  return "";
};

export const isPriceRangeDefined = (
  priceRange: PriceRange
): priceRange is { lower: number; upper: number } =>
  priceRange && priceRange?.lower != null && priceRange?.upper != null;
